import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import client, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";

export default function Register() {
    const [params] = useSearchParams();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [invite, setInvite] = useState((params.get("invite") || "").toUpperCase());
    const [loading, setLoading] = useState(false);
    const [inviteValid, setInviteValid] = useState(null);

    useEffect(() => {
        const c = (params.get("invite") || "").toUpperCase();
        if (c) {
            client
                .post("/auth/check-invite", { code: c })
                .then(() => setInviteValid(true))
                .catch(() => setInviteValid(false));
        }
        // eslint-disable-next-line
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        if (!invite.trim()) {
            toast.error("Invite code required. NoExcuse is invite-only.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await client.post("/auth/register", {
                name,
                email,
                password,
                invite_code: invite.trim(),
            });
            localStorage.setItem("noexcuse_token", data.token);
            window.location.assign("/onboarding");
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-shell min-h-screen px-6 pt-10 pb-12">
            <Link to="/" className="inline-flex items-center text-zinc-500 hover:text-white" data-testid="back-btn">
                <ArrowLeft size={18} />
                <span className="ml-2 label-cap text-xs">Back</span>
            </Link>

            <div className="mt-10">
                <span className="label-cap text-neon-orange text-xs flex items-center gap-1">
                    <Lock size={10} /> Invite Required
                </span>
                <h1 className="font-display font-black text-5xl uppercase tracking-tight mt-2">Sign Up</h1>
                <p className="text-zinc-400 mt-2">Build the body. Earn the badge.</p>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                    <label className="label-cap text-xs text-zinc-500">Invite Code</label>
                    <input
                        data-testid="register-invite"
                        type="text"
                        required
                        value={invite}
                        onChange={(e) => setInvite(e.target.value.toUpperCase())}
                        placeholder="NX-XXXXXXXX"
                        className="w-full mt-2 bg-charcoal-700 border px-4 py-3 text-white font-mono tracking-widest focus:outline-none"
                        style={{
                            borderColor:
                                inviteValid === true ? "#39FF14" : inviteValid === false ? "#FF2424" : "#333",
                        }}
                    />
                    {inviteValid === true && (
                        <div className="text-xs text-neon-green mt-1 label-cap">Valid Invite</div>
                    )}
                    {inviteValid === false && (
                        <div className="text-xs text-neon-orange mt-1 label-cap">Invite invalid or used up</div>
                    )}
                </div>
                <div>
                    <label className="label-cap text-xs text-zinc-500">Display Name</label>
                    <input
                        data-testid="register-name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full mt-2 bg-charcoal-700 border border-charcoal-500 px-4 py-3 text-white focus:border-neon-orange focus:outline-none"
                    />
                </div>
                <div>
                    <label className="label-cap text-xs text-zinc-500">Email</label>
                    <input
                        data-testid="register-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full mt-2 bg-charcoal-700 border border-charcoal-500 px-4 py-3 text-white focus:border-neon-orange focus:outline-none"
                    />
                </div>
                <div>
                    <label className="label-cap text-xs text-zinc-500">Password</label>
                    <input
                        data-testid="register-password"
                        type="password"
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full mt-2 bg-charcoal-700 border border-charcoal-500 px-4 py-3 text-white focus:border-neon-orange focus:outline-none"
                    />
                </div>
                <button
                    data-testid="register-submit"
                    type="submit"
                    disabled={loading}
                    className="btn-neon w-full py-4 text-lg disabled:opacity-50"
                >
                    {loading ? "Creating..." : "Start The Grind"}
                </button>
            </form>

            <div className="mt-8 text-center">
                <Link to="/login" className="text-zinc-400 hover:text-white text-sm" data-testid="goto-login">
                    Already a member? <span className="text-neon-green font-bold">Log in</span>
                </Link>
            </div>
        </div>
    );
}
